import { useState } from 'react';
import {
	ButtonBase,
	CircularProgress,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';

import {
	CheckmarkCircleIcon,
	CrossCircleIcon,
	CurrentlocationIcon,
	PdfColored,
} from '../../icons';

enum EAnimationState {
	None = 'NONE',
	Loading = 'LOADING',
	Finished = 'FINISHED',
	Error = 'ERROR',
}

export interface IMplInvoiceListItem {
	title: string;
	onSendInvoiceClick: () => Promise<void>;
	'data-testid'?: string;
}

function renderIcon(animationState: EAnimationState) {
	switch (animationState) {
		case EAnimationState.Loading:
			return <CircularProgress size={24} color="primary" />;
		case EAnimationState.Finished:
			return <CheckmarkCircleIcon />;
		case EAnimationState.Error:
			return <CrossCircleIcon />;
		case EAnimationState.None:
		default:
			return <CurrentlocationIcon />;
	}
}

export function MplInvoiceListItem({
	title,
	onSendInvoiceClick,
	'data-testid': dataTestId,
}: IMplInvoiceListItem) {
	const theme = useTheme();
	const [animationState, setAnimationState] = useState<EAnimationState>(
		EAnimationState.None
	);

	const handleClick = async () => {
		if (animationState !== EAnimationState.None) {
			return;
		}

		try {
			setAnimationState(EAnimationState.Loading);
			await onSendInvoiceClick();
			setAnimationState(EAnimationState.Finished);
		} catch {
			setAnimationState(EAnimationState.Error);
		} finally {
			// Reset to None after showing result briefly
			setTimeout(() => {
				setAnimationState(EAnimationState.None);
			}, 2000);
		}
	};

	return (
		<ButtonBase
			onClick={handleClick}
			aria-label={`Send invoice: ${title}`}
			aria-busy={animationState === EAnimationState.Loading}
			disabled={animationState !== EAnimationState.None}
			data-testid={dataTestId}
			sx={{
				width: '100%',
				textAlign: 'left',
				borderRadius: 1,
			}}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={2}
				width="100%"
				minWidth={0}
				padding={1}
			>
				<Stack flexShrink={0}>
					<PdfColored />
				</Stack>

				<Stack flex={1} minWidth={0}>
					<Typography
						variant="b2"
						color={theme.palette.primary.main}
						noWrap
						sx={{
							display: 'block',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{title}
					</Typography>
				</Stack>

				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						color: (theme) => theme.palette.primary.main,
						flexShrink: 0,
						width: '24px',
						height: '24px',
					}}
					data-testid={`${dataTestId}-icon`}
				>
					{renderIcon(animationState)}
				</Stack>
			</Stack>
		</ButtonBase>
	);
}
